import restClient from 'restClient'
import appStorage from 'storage/appStorage'
import { AttachedFile } from '../ChatContext'

const MB = 1024 * 1024
export const MAX_FILE_SIZE = 4.5 * MB // 4.5MB max per file
export const MAX_TOTAL_SIZE = 4.5 * MB // 4.5MB max total (same as individual limit)

export const hasUnuploadedFiles = (attachedFiles: AttachedFile[]): boolean => {
  // Only block sending if there are files still uploading or processing
  // Error files are excluded from blocking - they will be filtered out when sending
  return attachedFiles.some(
    (f) =>
      f.uploadState === 'uploading' ||
      f.uploadState === 'processing' ||
      f.uploadState === 'pending'
  )
}

export interface FileUploadResult {
  file_id: string
  file_name: string
  file_type: string
}

export interface FileValidationError {
  type: 'individual' | 'total' | 'upload'
  message: string
  files?: File[]
  failedFileNames?: string[]
}

interface UploadResponse {
  file_id: string
  file_type?: string
}

interface WrappedUploadResponse {
  data?: UploadResponse
  file_id?: string
  file_type?: string
}

const formatFileSize = (bytes: number): string => {
  return `${(bytes / MB).toFixed(1)}MB`
}

const createFormData = (file: File, orgId: number): FormData => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('org_id', orgId.toString())
  return formData
}

const parseUploadResponse = (response: unknown): UploadResponse => {
  // Handle both wrapped (response.data) and unwrapped (response) formats
  const wrapped = response as WrappedUploadResponse
  const data = wrapped.data || wrapped

  if (!data || typeof data !== 'object' || !('file_id' in data)) {
    throw new Error('Invalid response format: missing file_id')
  }

  return {
    file_id: String(data.file_id),
    file_type: data.file_type || 'file',
  }
}

export const validateFiles = (files: File[]): FileValidationError | null => {
  if (files.length === 0) {
    return {
      type: 'individual',
      message: 'No files provided',
      files: [],
    }
  }

  const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE)
  if (oversizedFiles.length > 0) {
    return {
      type: 'individual',
      message: `File(s) exceed ${formatFileSize(MAX_FILE_SIZE)} limit: ${oversizedFiles.map((f) => f.name).join(', ')}`,
      files: oversizedFiles,
    }
  }

  // Check total size
  const totalSize = files.reduce((sum, file) => sum + file.size, 0)
  if (totalSize > MAX_TOTAL_SIZE) {
    return {
      type: 'total',
      message: `Total file size (${formatFileSize(totalSize)}) exceeds ${formatFileSize(MAX_TOTAL_SIZE)} limit`,
      files,
    }
  }

  return null
}

interface UploadFailure {
  fileName: string
  error: Error
}

export const uploadFiles = async (
  files: File[],
  osAssistantCode: string,
  orgId: number
): Promise<FileUploadResult[]> => {
  if (files.length === 0) {
    return []
  }

  const restClientInstance = restClient(`${window.API_ROOT}/api`)
  const uploadPromises = files.map(async (file) => {
    const formData = createFormData(file, orgId)

    try {
      const response = await restClientInstance('CUSTOM_POST', 'custom', {
        url: `ada/${osAssistantCode}/upload-file/`,
        data: formData,
      })

      const uploadData = parseUploadResponse(response.data || response)

      return {
        file_id: uploadData.file_id,
        file_name: file.name,
        file_type: uploadData.file_type || 'file',
      }
    } catch (error: any) {
      // Extract error message from various possible error formats
      let errorMessage = 'Upload failed'
      if (error?.response?.data) {
        const errorData = error.response.data
        errorMessage = errorData.message || errorData.error || errorMessage
      } else if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      throw new Error(errorMessage)
    }
  })

  const results = await Promise.allSettled(uploadPromises)

  const failures: UploadFailure[] = []
  const successes: FileUploadResult[] = []

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      successes.push(result.value)
    } else {
      failures.push({
        fileName: files[index].name,
        error: result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
      })
    }
  })

  if (failures.length > 0) {
    const failedNames = failures.map((f) => f.fileName)
    const errorMessages = failures.map((f) => `${f.fileName}: ${f.error.message}`)
    const errorMessage =
      failures.length === files.length
        ? `Failed to upload files: ${failedNames.join(', ')}. ${errorMessages.join('; ')}`
        : `Failed to upload some files: ${failedNames.join(', ')}. ${errorMessages.join('; ')}`
    throw new Error(errorMessage)
  }

  return successes
}

export const uploadSingleFile = async (
  file: File,
  osAssistantCode: string,
  orgId: number
): Promise<FileUploadResult> => {
  const results = await uploadFiles([file], osAssistantCode, orgId)
  return results[0]
}

export const uploadSingleFileWithProgress = async (
  file: File,
  osAssistantCode: string,
  orgId: number,
  onProgress?: (progress: number) => void
): Promise<FileUploadResult> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = createFormData(file, orgId)

    const url = `${window.API_ROOT}/api/ada/${osAssistantCode}/upload-file/`
    const token = appStorage.getToken()

    if (!token) {
      reject(new Error('Authentication token not available'))
      return
    }

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const progress = Math.round((e.loaded / e.total) * 100)
        onProgress(Math.min(progress, 100)) // Ensure progress doesn't exceed 100%
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText)
          const uploadData = parseUploadResponse(response)
          resolve({
            file_id: uploadData.file_id,
            file_name: file.name,
            file_type: uploadData.file_type || 'file',
          })
        } catch (error) {
          const parseError = error instanceof Error ? error.message : 'Unknown error'
          reject(new Error(`Failed to parse response: ${parseError}`))
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText)
          // Extract error message from various possible response formats
          const errorMessage =
            errorResponse.message ||
            errorResponse.error ||
            errorResponse.data?.message ||
            errorResponse.data?.error ||
            `Upload failed with status ${xhr.status}`
          reject(new Error(errorMessage))
        } catch {
          // If response isn't JSON, try to extract text or use status
          const responseText = xhr.responseText || ''
          const errorMessage = responseText || `Upload failed with status ${xhr.status}`
          reject(new Error(errorMessage))
        }
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    xhr.addEventListener('timeout', () => {
      reject(new Error('Upload timeout'))
    })

    xhr.open('POST', url)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.send(formData)
  })
}

export const uploadFilesWithValidation = async (
  files: File[],
  osAssistantCode: string,
  orgId: number
): Promise<{ success: true; results: FileUploadResult[] } | { success: false; error: FileValidationError }> => {
  const validationError = validateFiles(files)
  if (validationError) {
    return { success: false, error: validationError }
  }

  try {
    const results = await uploadFiles(files, osAssistantCode, orgId)
    return { success: true, results }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to upload files'

    const failedFileNames: string[] = []
    const match = errorMessage.match(/Failed to upload (?:some )?files: ([^.]+)/)
    if (match) {
      failedFileNames.push(...match[1].split(',').map((name) => name.trim()))
    }

    return {
      success: false,
      error: {
        type: 'upload',
        message: errorMessage,
        failedFileNames: failedFileNames.length > 0 ? failedFileNames : undefined,
      },
    }
  }
}
